import Service from '@ember/service';
import { QueryManager } from '@apollo/client/core/QueryManager';
import { service } from '@ember/service';
import { SlAlert } from '@shoelace-style/shoelace';
import { task } from 'ember-concurrency';
import { queryManager } from 'ember-apollo-client';
import studentsQuery from 'dashboard/gql/queries/students.gql';
import { convertToCsv } from 'dashboard/utils/convert-to-csv';
import { isPending, isDeactivated } from 'dashboard/utils';
import { escapeHtml } from 'dashboard/utils/escape-html';
import { type IntlService } from 'ember-intl';
import type { School, Student } from 'dashboard/types';
import type ToastService from 'dashboard/services/toast';
import type { StudentListModel } from 'dashboard/controllers/dashboard/account/students';

/* number of students to fetch at a time (to not overload service or prevent
   a malformed JSON response) */
const STUDENTS_PER_EXPORT_PAGE = 150;

// match student default order so export matches
const DEFAULT_ORDER_BY = 'accountStatus-asc, lastName';

type StudentsQueryResult = { students: StudentListModel['students'] };

export default class ExportStudentsService extends Service {
  @queryManager declare apollo: QueryManager<Service>;
  @service declare intl: IntlService;
  @service declare toast: ToastService;

  startExport(
    purchaser: School,
    currentStatuses: Array<string>,
    currentYearGroup: string | undefined,
  ) {
    if (!purchaser) return;
    return this.exportStudentsTask.perform(
      purchaser,
      currentStatuses,
      currentYearGroup,
    );
  }

  // fetch page-by-page until there are no more pages
  exportStudentsTask = task(
    { drop: true },
    async (
      purchaser: School,
      currentStatuses: Array<string>,
      currentYearGroup: string | undefined,
    ) => {
      const allStudents: Student[] = [];
      let cancelled = false;

      // show the toast right away - it will update on each successful query
      const progressAlert: SlAlert = this.#createToast(
        this.intl.t('Exporting students…'),
      );

      // listen for closing alert to cancel fetch
      progressAlert.addEventListener(
        'sl-hide',
        () => {
          cancelled = true;
        },
        { once: true },
      );

      try {
        let after = '';
        let hasNextPage = true;
        let total: number | undefined;

        const searchParams: {
          purchaserId: string;
          orderBy: string;
          accountStatuses?: string[];
          yearGroup?: string[];
        } = {
          purchaserId: purchaser.id,
          orderBy: DEFAULT_ORDER_BY,
        };

        if (currentStatuses.length > 0) {
          searchParams.accountStatuses = currentStatuses;
        }

        if (currentYearGroup) searchParams.yearGroup = [currentYearGroup];

        // only loop if there are more pages to fetch and they haven't cancelled
        while (hasNextPage && !cancelled) {
          // @ts-expect-error how to type apollo query responses?
          const result: StudentsQueryResult = await this.apollo.query({
            query: studentsQuery,
            variables: {
              first: STUDENTS_PER_EXPORT_PAGE,
              after,
              searchParams,
            },
            fetchPolicy: 'network-only',
          });

          if (cancelled) break;
          if (!total) total = result.students.queryResultCount;

          allStudents.push(...result.students.edges.map((edge) => edge.node));
          hasNextPage = result.students.pageInfo.hasNextPage;
          after = result.students.pageInfo.endCursor;

          this.#updateToastMessage(
            progressAlert,
            this.intl.t('Exporting students… {count} of {total}', {
              count: allStudents.length,
              total,
            }),
          );
        }
      } catch (reason) {
        // TODO: possible to retry with smaller STUDENTS_PER_EXPORT_PAGE ?
        // (especially if a JSON error is the captured reason?)
        if (cancelled) return;
        progressAlert.hide();
        this.toast.warn(reason, 'Error exporting students.');
        return Promise.reject(reason);
      }

      if (cancelled) return;

      progressAlert.hide();

      // do not export CSV if there are no students.
      if (allStudents.length === 0) {
        const hasFilters = currentStatuses.length > 0 || currentYearGroup;
        this.toast.notify(
          this.intl.t(
            hasFilters
              ? 'There are no students with the selected filters'
              : 'There are no students in this school',
          ),
          'neutral',
        );
        return;
      }

      this.exportCsv(allStudents, purchaser);
    },
  );

  exportCsv(students: Student[], purchaser: School) {
    const header = [
      this.intl.t('Name'),
      this.intl.t('Username'),
      this.intl.t('Email'),
      this.intl.t('Year Group'),
      this.intl.t('Last active'),
      this.intl.t('Status'),
    ];
    const rows = students.map((student) => this.#buildRow(student));

    const csvUrl = convertToCsv([header, ...rows]);

    // escape the school name since it's directly inserted, just in case
    const safeName = escapeHtml(purchaser.name);
    const filename = `${safeName} - Students.csv`;
    const reportMessage = this.intl.t('{name} - Students.csv', {
      name: safeName,
    });
    const reportLink = `<a href="${csvUrl}" download="${filename}">${reportMessage}</a>`;

    const successAlert = this.#createToast(reportLink, 'success');

    // the CSV only needs to live as long as the toast does; free it once the
    // user is done with the download link (or dismisses the toast)
    successAlert.addEventListener(
      'sl-after-hide',
      () => URL.revokeObjectURL(csvUrl),
      { once: true },
    );
  }

  /**
   * Build a toast alert with an icon (matching the look of the QR export's
   * toasts), append it to the DOM, and show it. The message lives in its own
   * element so it can be swapped out later via `#updateToastMessage` without
   * touching (and thereby restarting) the icon/spinner.
   */
  #createToast(
    message: string,
    state: 'progress' | 'success' = 'progress',
  ): SlAlert {
    const icon =
      state === 'success'
        ? `<sl-icon library="showbie" name="check" slot="icon" class="w-8 h-8 text-green-600"></sl-icon>`
        : `<sl-spinner slot="icon"></sl-spinner>`;

    const alert = Object.assign(document.createElement('sl-alert'), {
      variant: 'primary',
      closable: true,
    });

    alert.innerHTML = `${icon} <span data-toast-message>${message}</span>`;
    alert.classList.add('lbx-toast');

    document.body.append(alert);
    alert.toast();

    return alert;
  }

  #updateToastMessage(alert: SlAlert, message: string) {
    const messageEl = alert.querySelector('[data-toast-message]');
    if (messageEl) messageEl.textContent = message;
  }

  #buildRow(student: Student): string[] {
    const status = isPending(student)
      ? this.intl.t('Pending')
      : isDeactivated(student)
        ? this.intl.t('Deactivated')
        : this.intl.t('Member');

    const lastActive =
      !isPending(student) && student.lastUserAccess
        ? this.intl.formatDate(new Date(student.lastUserAccess), {
            format: 'dmy',
          })
        : '';

    return [
      `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim(),
      student.username ?? '',
      student.email ?? '',
      student.yearGroup ?? '',
      lastActive,
      status,
    ];
  }
}

declare module '@ember/service' {
  interface Registry {
    'export-students': ExportStudentsService;
  }
}
